import React, { useState } from 'react';

const EducationSection = () => {
  const [activeCategory, setActiveCategory] = useState('corporate');

  const categories = {
    corporate: {
      title: '기업 맞춤 교육',
      description: '임원 및 실무자를 대상으로 한 AI 전략 워크숍과 사내 AI 활용 교육을 제공합니다.',
      features: [
        '임원/실무자 대상 맞춤 커리큘럼',
        'AI 전략 워크숍 및 의사결정 지원',
        '사내 AI 활용 교육 프로그램',
        '산업별 특화 사례 중심 학습',
        '교육 후 지속적인 팔로업 지원'
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    online: {
      title: '온라인 강의',
      description: '자기주도 학습이 가능한 동영상 커리큘럼과 프롬프트 엔지니어링 과정을 제공합니다.',
      features: [
        '자기주도 학습 환경 제공',
        '체계적인 동영상 커리큘럼',
        '프롬프트 엔지니어링 집중 과정',
        '실시간 Q&A 및 피드백',
        '수료 후 커뮤니티 참여 가능'
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    },
    handson: {
      title: '핸즈온 랩',
      description: '실습 중심의 교육으로 챗봇 구축 체험과 AI 도구 활용 실전 능력을 키웁니다.',
      features: [
        '실습 중심의 체험형 교육',
        '나만의 AI 챗봇 직접 구축',
        'AI 도구 활용 실전 프로젝트',
        '소그룹 멘토링 및 코칭',
        '실무 적용 가능한 결과물 완성'
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      )
    }
  };

  const curriculum = [
    { step: 1, title: 'AI 기초 이해', description: 'Generative AI의 개념, 원리, 주요 모델 이해 및 활용 분야 탐색' },
    { step: 2, title: '프롬프트 엔지니어링', description: '효과적인 프롬프트 작성법, 고급 기법, 다양한 AI 모델별 최적화 전략' },
    { step: 3, title: 'AI 도구 활용 실전', description: 'ChatGPT, Claude, Midjourney 등 주요 AI 도구의 실무 활용법 습득' },
    { step: 4, title: '업무 자동화 구축', description: 'AI 기반 워크플로우 자동화, API 연동, 반복 업무 효율화 실습' },
    { step: 5, title: '프로젝트 완성 & 발표', description: '실전 프로젝트 수행 및 결과물 발표, 피드백과 개선 방향 도출' }
  ];

  return (
    <section id="education" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* 섹션 제목 */}
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            교육 서비스
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
            실전 중심 <span className="text-primary">AI 교육</span> 프로그램
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            GenAI Dev는 기업과 개인을 위한 체계적인 AI 교육 프로그램을 제공합니다.
            기초부터 실전까지, 현장에서 바로 활용 가능한 AI 역량을 키워드립니다.
          </p>
        </div>

        {/* 교육 카테고리 탭 */}
        <div className="flex flex-wrap justify-center mb-10 gap-2">
          {Object.keys(categories).map((key) => (
            <button
              key={key}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === key
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => setActiveCategory(key)}
            >
              {categories[key].title}
            </button>
          ))}
        </div>

        {/* 선택된 카테고리 상세 */}
        <div className="bg-light rounded-2xl p-8 md:p-10 shadow-md">
          <div className="flex flex-col md:flex-row gap-10">
            <div className="md:w-1/2">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6 text-primary">
                {categories[activeCategory].icon}
              </div>
              <h3 className="text-2xl font-bold text-dark mb-4">{categories[activeCategory].title}</h3>
              <p className="text-gray-600 mb-6">{categories[activeCategory].description}</p>

              <h4 className="font-bold text-dark mb-4">교육 특징</h4>
              <ul className="space-y-3">
                {categories[activeCategory].features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 카테고리별 우측 패널 */}
            <div className="md:w-1/2 bg-white rounded-xl shadow-sm p-6">
              {activeCategory === 'corporate' && (
                <div className="h-full">
                  <h4 className="font-bold text-dark mb-4">기업 교육 진행 방식</h4>
                  <div className="space-y-4">
                    <div className="bg-light rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3 flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <h5 className="font-bold text-dark">사전 진단</h5>
                      </div>
                      <p className="text-gray-600 text-sm ml-11">기업 현황 및 교육 니즈 파악, 맞춤 커리큘럼 설계</p>
                    </div>
                    <div className="bg-light rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3 flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <h5 className="font-bold text-dark">집중 워크숍</h5>
                      </div>
                      <p className="text-gray-600 text-sm ml-11">임원/실무자 대상 AI 전략 및 활용 집중 교육</p>
                    </div>
                    <div className="bg-light rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3 flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h5 className="font-bold text-dark">성과 측정</h5>
                      </div>
                      <p className="text-gray-600 text-sm ml-11">교육 효과 분석 및 후속 지원 계획 수립</p>
                    </div>
                  </div>
                </div>
              )}

              {activeCategory === 'online' && (
                <div className="h-full">
                  <h4 className="font-bold text-dark mb-4">온라인 학습 환경</h4>
                  <div className="space-y-4">
                    <div className="bg-light rounded-lg p-4 flex items-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h5 className="font-bold text-dark text-sm">언제 어디서나</h5>
                        <p className="text-gray-600 text-xs">시간과 장소에 구애받지 않는 자기주도 학습</p>
                      </div>
                    </div>
                    <div className="bg-light rounded-lg p-4 flex items-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </div>
                      <div>
                        <h5 className="font-bold text-dark text-sm">반복 학습</h5>
                        <p className="text-gray-600 text-xs">무제한 복습이 가능한 동영상 커리큘럼</p>
                      </div>
                    </div>
                    <div className="bg-light rounded-lg p-4 flex items-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <h5 className="font-bold text-dark text-sm">실시간 소통</h5>
                        <p className="text-gray-600 text-xs">전문 강사와의 실시간 Q&A 세션 제공</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeCategory === 'handson' && (
                <div className="h-full">
                  <h4 className="font-bold text-dark mb-4">핸즈온 랩 프로젝트 예시</h4>
                  <div className="space-y-4">
                    <div className="bg-light rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-bold text-dark text-sm">AI 챗봇 구축</h5>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">인기</span>
                      </div>
                      <p className="text-gray-600 text-sm">나만의 비즈니스 챗봇을 직접 설계하고 구축하는 실습</p>
                    </div>
                    <div className="bg-light rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-bold text-dark text-sm">문서 자동화 시스템</h5>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">실전</span>
                      </div>
                      <p className="text-gray-600 text-sm">AI를 활용한 보고서, 이메일, 제안서 자동 작성 시스템 구축</p>
                    </div>
                    <div className="bg-light rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-bold text-dark text-sm">데이터 분석 자동화</h5>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">고급</span>
                      </div>
                      <p className="text-gray-600 text-sm">AI 기반 데이터 분석 파이프라인 구축 및 인사이트 도출</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 커리큘럼 단계 */}
        <div className="mt-20">
          <h3 className="text-2xl font-bold text-dark mb-10 text-center">
            <span className="text-primary">5단계</span> 커리큘럼
          </h3>

          <div className="space-y-4">
            {curriculum.map((item) => (
              <div key={item.step} className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center mr-3 flex-shrink-0 font-bold">
                  {item.step}
                </div>
                <div>
                  <h5 className="font-bold text-dark">{item.title}</h5>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 가격 카드 */}
        <div className="mt-20 flex justify-center">
          <div className="bg-light rounded-2xl p-8 md:p-10 shadow-md max-w-md w-full text-center relative overflow-hidden">
            {/* 프로모션 뱃지 */}
            <div className="absolute top-4 right-4">
              <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-xs font-bold animate-pulse">
                프로모션 진행 중
              </span>
            </div>

            <h3 className="text-xl font-bold text-dark mb-2 mt-4">AI 교육 프로그램</h3>
            <p className="text-gray-500 text-sm mb-6">3개월 과정</p>

            {/* 가격 */}
            <div className="mb-6">
              <p className="text-gray-400 text-lg line-through mb-1">400만원</p>
              <p className="text-primary text-3xl font-bold mb-1">350만원</p>
              <div className="flex items-center justify-center gap-2">
                <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">
                  12.5% 할인
                </span>
                <span className="text-gray-500 text-xs">VAT 포함</span>
              </div>
            </div>

            {/* 포함 내역 */}
            <ul className="text-left space-y-2 mb-8">
              <li className="flex items-center text-gray-600 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                5단계 체계적 커리큘럼
              </li>
              <li className="flex items-center text-gray-600 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                실습 프로젝트 포함
              </li>
              <li className="flex items-center text-gray-600 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                전문 강사 1:1 멘토링
              </li>
              <li className="flex items-center text-gray-600 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                수료증 발급
              </li>
            </ul>

            {/* CTA 버튼 */}
            <a
              href="#contact"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors w-full"
            >
              교육 상담 신청하기
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EducationSection;
